import { DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, CardinalApiState, guard } from '../services/auth.api'
import { InitialEvaluationData, MedicalOverview, PatientData, WellBeingSummary } from '../../components/Patients/components/MedicalReportPDF/types'
import { DecryptedService } from '@icure/cardinal-sdk'
import { VersionedData } from '@icure/form/generic'
import { resolveFormTemplate } from './formTemplateUtils'
import { loadChildrenRecursively } from './formApi'
import { ContactFilters } from '@icure/cardinal-sdk/filters.mjs'
import { loadFromIterator } from './utils'
import { extractReportData } from '../../helpers/reportDataExtraction'

export const pdfReportApiRtk = createApi({
  reducerPath: 'pdfReportApi',
  tagTypes: ['PdfReport'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    generatePdfReport: builder.query<
      {
        patientData: PatientData | undefined
        medicalOverview: MedicalOverview | undefined
        initialEvaluationData: InitialEvaluationData | undefined
        wellBeingSummary: WellBeingSummary | undefined
      },
      {
        language: string
        patient: DecryptedPatient
        formId: string | undefined
      }
    >({
      async queryFn({ language, patient, formId }, { getState }) {
        const sdk = await cardinalApi(getState)

        const {
          cardinalApi: { user },
        } = getState() as { cardinalApi: CardinalApiState }

        return guard(
          [sdk?.contact, sdk?.form, sdk?.healthcareParty, patient, user?.healthcarePartyId, formId],
          async ([contactApi, formApi, healthcarePartyApi, patient, hcPartyId, formId]): Promise<{
            patientData: PatientData
            medicalOverview: MedicalOverview
            initialEvaluationData: InitialEvaluationData
            wellBeingSummary: WellBeingSummary
          }> => {
            const hcpOrParentId = (await healthcarePartyApi.getHealthcareParty(hcPartyId))?.parentId ?? hcPartyId
            const rootForm = formId ? await formApi.getForm(formId) : undefined
            const form = await resolveFormTemplate(formApi, rootForm?.formTemplateId)

            const formIds = await loadChildrenRecursively(formApi, hcpOrParentId, formId).then((forms) => forms.map((f) => f.id))
            const contactsHistory = formId
              ? await loadFromIterator(await contactApi.filterContactsBy(ContactFilters.byFormIdsForDataOwner(hcpOrParentId, [formId, ...formIds])), 10000)
              : []
            const indexedServices: {
              [id: string]: import('@icure/form/generic').Version<DecryptedService>[]
            } = contactsHistory.reduce((acc, ctc) => {
              return (
                ctc.services
                  ?.filter((s) => ctc.subContacts?.some((sc) => sc.formId === formId && sc.services?.some((sss) => sss.serviceId === s.id)))
                  ?.reduce(
                    (acc, s) =>
                      s.id
                        ? {
                            ...acc,
                            [s.id]: (acc[s.id] ?? (acc[s.id] = []))
                              .concat({
                                revision: ctc.rev ?? null,
                                modified: s.modified ?? ctc.modified ?? s.created ?? ctc.created,
                                value: s,
                              })
                              .sort((a, b) => -((a.modified ?? 0) < (b.modified ?? 0) ? -1 : (a.modified ?? 0) > (b.modified ?? 0) ? 1 : 0)),
                          }
                        : acc,
                    acc,
                  ) ?? acc
              )
            }, {} as VersionedData<DecryptedService>)

            return extractReportData({ indexedServices, patient, language, form })
          },
          {
            patientData: undefined,
            medicalOverview: undefined,
            initialEvaluationData: undefined,
            wellBeingSummary: undefined,
          },
        )
      },
    }),
  }),
})

export const { useGeneratePdfReportQuery, useLazyGeneratePdfReportQuery } = pdfReportApiRtk
